import "./contextBarView.scss";

import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { Motion, spring } from "react-motion";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import { withXScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import { withZoomContext } from "../../components/scrollable/ZoomContext";
import timeoutRates from "../../constants/timeoutRates";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import { stringFromDate, stringFromDateRange } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { immutableRecords, PropTypeShapes } from "../../types";
import { disableWhenIos } from "../../utils/codeSplit";
import { getScreenPointerPositionFromEvent } from "../../utils/mouseUtils";
import uniqueId from "../../utils/uniqueId";
import ContextBarCanvas from "./ContextBarCanvas";
import ContextBarLens from "./ContextBarLens";
import selectors from "./selectors";

const MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR = 10.0;
const AUTOSCROLL_BORDER = 20;

const calcLensLeft = state => Math.round((state.mainViewLeftTs - state.scrollLeftTs) / state.unitScale);
const calcLensRight = state => Math.round((state.mainViewRightTs - state.scrollLeftTs) / state.unitScale);
const calcLensWidth = state => calcLensRight(state) - calcLensLeft(state);

const calcLensLeftTsMinimum = (canvas, mainViewUnitScale) => Math.round(canvas.min * mainViewUnitScale);
const calcLensRightTsMaximum = (canvas, mainViewUnitScale) => Math.round(canvas.max * mainViewUnitScale);
const calcLensLeftTsMaximum = (canvas, mainViewUnitScale, containerWidth) => {
  return Math.round(canvas.max * mainViewUnitScale - containerWidth * mainViewUnitScale);
};

const calcCanvasLeftTsMaximum = (canvas, mainViewUnitScale, containerWidth) => {
  const ratio = Math.min(MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR, canvas.range / containerWidth);
  const contextUnitScale = mainViewUnitScale * ratio;
  return Math.round(canvas.max * mainViewUnitScale - containerWidth * contextUnitScale);
};

const calcTargetScrollState = (mainViewScrollLeft, mainViewUnitScale, canvas, containerWidth) => {
  const mainViewLeftTs = mainViewScrollLeft * mainViewUnitScale;
  const mainViewRightTs = (mainViewScrollLeft + containerWidth) * mainViewUnitScale;

  const ratio = Math.min(MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR, canvas.range / containerWidth);
  const contextUnitScale = mainViewUnitScale * ratio;

  const canvasMinTs = calcLensLeftTsMinimum(canvas, mainViewUnitScale);
  let contextLeftTs = null;

  if (ratio < MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR) {
    contextLeftTs = canvasMinTs;
  } else {
    const origCenterTs = mainViewUnitScale * (mainViewScrollLeft + containerWidth / 2);
    const adjustedCenterPixel = origCenterTs / contextUnitScale;
    const contextLeftPixel = adjustedCenterPixel - containerWidth / 2;
    const canvasMaxLeftTs = calcCanvasLeftTsMaximum(canvas, mainViewUnitScale, containerWidth);

    // Calculate left position that will center our lens
    contextLeftTs = Math.round(contextLeftPixel * contextUnitScale);
    // But don't let it go past our edges... that way the lens moves to the edge as it nears boundary
    if (contextLeftTs > canvasMaxLeftTs) {
      contextLeftTs = canvasMaxLeftTs;
    }
    if (contextLeftTs < canvasMinTs) {
      contextLeftTs = canvasMinTs;
    }
  }

  return {
    scrollLeftTs: contextLeftTs,
    unitScale: contextUnitScale,
    mainViewLeftTs,
    mainViewRightTs
  };
};

class ContextBarView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      forceChange: null
    };
    // We don't use state to track this since we are doing some stuff outside of renders
    this._scrollState = immutableRecords.ContextScrollPositionRecord({
      mainViewLeftTs: 0,
      mainViewRightTs: 0,
      scrollLeftTs: 0,
      unitScale: 1,
      contextUnitScale: MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR
    });

    // We cannot rely on state to know when we intend to animate, because we want to know when we are
    // about to animate but the state change hasn't triggered. So instead, we will track the variable
    // here, and simply use the state.forceChange variable to trigger a re-render when required
    this._animate = false;

    // This gets set only when we want to trigger an animation, which must be hooked up via
    // componentDidUpdate()
    this._scrollStateChangeAfterUpdate = null;

    this._domNode = null;
    this._componentId = uniqueId();

    this._scrollListeners = {};
    this.updateScrollState = this.updateScrollState.bind(this);
    this.notifyListenersNow = this.notifyListenersNow.bind(this);
    this.registerScrollListener = this.registerScrollListener.bind(this);
    this.unregisterScrollListener = this.unregisterScrollListener.bind(this);

    this.requestNodeClientLeft = this.requestNodeClientLeft.bind(this);
    this.lensDrag = this.lensDrag.bind(this);

    this.lensDragStart = this.lensDragStart.bind(this);
    this.lensDragEnd = this.lensDragEnd.bind(this);
    this.resizeDrag = this.resizeDrag.bind(this);
    this.resizeDragStart = this.resizeDragStart.bind(this);
    this.resizeDragEnd = this.resizeDragEnd.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);

    // Throttled versions of functions
    this.throttledLensDrag = _.throttle(this.lensDrag, timeoutRates.SCROLL_THROTTLE, {
      leading: false,
      trailing: true
    });
    this.throttledResizeDrag = _.throttle(this.resizeDrag, timeoutRates.ZOOM_THROTTLE, {
      leading: false,
      trailing: true
    });

    this.startAnimation = this.startAnimation.bind(this);
    this.stopAnimation = this.stopAnimation.bind(this);
    this.debouncedStartAnimation = _.debounce(this.startAnimation, timeoutRates.CONTEXT_BAR_RESET);
    this.debouncedStopAnimation = _.debounce(this.stopAnimation, 0);

    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollStop.bind(this)
    };

    this._contextLensTooltipRef = null;
  }

  componentDidMount() {
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
  }

  componentDidUpdate() {
    // This is normally frowned upon, but here we actually do want to drive another render.
    // When we are animating the context bar into position after a drag:
    // 1) this._scrollStateChangeAfterUpdate is set to the final value we want to show
    // 2) We use state.forceChange to make it render with the old values (so React.Motion has the correct values, since we have been working around them)
    // 3) After that render, we then catch it here and update the state again to trigger a second render
    // This guarantees that React-Motion will animate from the correct starting position, rather than a stale
    // state that is not up to date with any direct transforms we applied for speed.
    if (this._scrollStateChangeAfterUpdate) {
      // Turn on animation and trigger re-update
      this._animate = true;
      // Update with the correct values
      this.updateScrollState(this._scrollStateChangeAfterUpdate);
      // Force a re-render to trigger the animation
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        forceChange: uniqueId()
      });
      // And clear the state for next time
      this._scrollStateChangeAfterUpdate = null;
    }
  }

  componentWillUnmount() {
    this.throttledLensDrag.cancel();
    this.throttledResizeDrag.cancel();
    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
  }

  registerScrollListener(id, listener) {
    this._scrollListeners[id] = listener;
  }

  unregisterScrollListener(id) {
    delete this._scrollListeners[id];
  }

  notifyContainerOfViewport() {
    this.props.xScrollActions.jumpToViewport(this._mainViewScrollLeft, null, this._mainViewZoom);
  }

  onLiveScrollStart() {
    const left = this.props.containerRect.left;
    const top = this.props.containerRect.top;
    this._domNode.style.left = `${left}px`;
    this._domNode.style.top = `${top}px`;
  }

  onLiveScrollUpdate(scrollLeft, _scrollTop) {
    const unitScale = this.props.zoom;
    this._mainViewScrollLeft = scrollLeft;
    this._mainViewZoom = unitScale;
    let changes = calcTargetScrollState(
      this._mainViewScrollLeft,
      this._mainViewZoom,
      this.props.xScrollPosition.canvas,
      this.props.containerRect.width
    );
    if (this._lensDragging || this._resizeDragging || this._pendingZoomAndScroll) {
      changes = _.pick(changes, ["mainViewLeftTs", "mainViewRightTs"]);
      this._pendingZoomAndScroll = true;
    }
    this.updateScrollState(changes);
  }

  onLiveScrollStop() {}

  startAnimation() {
    this._pendingZoomAndScroll = false;

    this._scrollStateChangeAfterUpdate = calcTargetScrollState(
      this._mainViewScrollLeft,
      this._mainViewZoom,
      this.props.xScrollPosition.canvas,
      this.props.containerRect.width
    );

    // Force an update so that it will recognise the current values before starting the animation from there
    this._animate = false;
    this.setState({
      forceChange: uniqueId()
    });
  }

  stopAnimation() {
    this._animate = false;
    this.setState({
      forceChange: uniqueId()
    });
    this.clearDateTooltip();
  }

  updateScrollState(changes, insideRaf = false) {
    // First, update our scrollState
    this._scrollState = this._scrollState.merge(changes);

    if (insideRaf) {
      this.notifyListenersNow();
      return;
    }
    if (this._scrollRafWaiting) {
      return;
    }
    this._scrollRafWaiting = window.requestAnimationFrame(() => {
      this._scrollRafWaiting = null;
      this.notifyListenersNow();
    });
  }

  notifyListenersNow() {
    if (!this._animate) {
      _.each(this._scrollListeners, listener => listener(this._scrollState));
    }
  }

  requestNodeClientLeft() {
    return this._domNode.getBoundingClientRect().left;
  }

  lensDragStart(x) {
    this.stopAnimation();
    this._lensDragStartX = x;
    this._lensDragStartLensLeft = calcLensLeft(this._scrollState);
    this._lensDragging = true;
  }

  lensDrag(x) {
    const thisDelta = x - this._lensDragLastX;
    if (thisDelta !== 0) {
      this._lensDragLastMovementDelta = thisDelta;
    }
    this._lensDragLastX = x;
    const offset = x - this._lensDragStartX;
    const lensLeft = this._lensDragStartLensLeft + offset;
    let absoluteLensLeftTs = lensLeft * this._scrollState.unitScale + this._scrollState.scrollLeftTs;

    const minLensLeftTs = calcLensLeftTsMinimum(this.props.xScrollPosition.canvas, this.props.zoom);
    const maxLensLeftTs = calcLensLeftTsMaximum(
      this.props.xScrollPosition.canvas,
      this.props.zoom,
      this.props.containerRect.width
    );

    absoluteLensLeftTs = Math.max(minLensLeftTs, absoluteLensLeftTs);
    absoluteLensLeftTs = Math.min(maxLensLeftTs, absoluteLensLeftTs);

    this._lensDragLastLeftTs = absoluteLensLeftTs;

    // Show tooltip
    const leftDate = dateWithTimestamp(this.props.calendar, Math.round(this._lensDragLastLeftTs));
    const leftDateRounded = dateFunctions.roundedDateForScale(
      this.props.calendar,
      leftDate,
      this._scrollState.unitScale * MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR
    );
    const rightDate = dateWithTimestamp(this.props.calendar, Math.round(this._scrollState.mainViewRightTs));
    const rightDateRounded = dateFunctions.roundedDateForScale(
      this.props.calendar,
      rightDate,
      this._scrollState.unitScale * MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR
    );
    const dateRangeString = stringFromDateRange(this.props.calendar, leftDateRounded, rightDateRounded);
    this.updateLeftDateTooltip(dateRangeString);

    this._mainViewZoom = this.props.zoom;
    this._mainViewScrollLeft = absoluteLensLeftTs / this._mainViewZoom;
    this.notifyContainerOfViewport();

    if (this.lensDragEdgeAnimationScrollDelta() !== 0) {
      this.lensAnimateAtEdge();
    }
  }

  lensDragEdgeAnimationScrollDelta() {
    // If the lens is touching the left edge we need to repeat the drag with an increasing offset...
    if (!this._lensDragging) {
      return 0;
    }

    const minTs = calcLensLeftTsMinimum(this.props.xScrollPosition.canvas, this.props.zoom);
    const maxTs = calcCanvasLeftTsMaximum(
      this.props.xScrollPosition.canvas,
      this.props.zoom,
      this.props.containerRect.width
    );
    const atLeftEdge = this.props.hasLowerRangeLimit && this._lensDragLastLeftTs <= minTs;
    const atRightEdge = this.props.hasUpperRangeLimit && this._lensDragLastLeftTs >= maxTs;

    if (!atLeftEdge && this._lensDragLastX < calcLensWidth(this._scrollState) && this._lensDragLastMovementDelta <= 0) {
      return -5;
    } else if (
      !atRightEdge &&
      this._lensDragLastX > this.props.containerRect.width - calcLensWidth(this._scrollState) &&
      this._lensDragLastMovementDelta >= 0
    ) {
      return 5;
    }
    return 0;
  }

  lensAnimateAtEdge() {
    if (this._rafWaiting) {
      return;
    }
    this._rafWaiting = window.requestAnimationFrame(() => {
      this._rafWaiting = null;
      const offsetDelta = this.lensDragEdgeAnimationScrollDelta();
      const offsetDeltaTs = offsetDelta * this._scrollState.unitScale;
      if (offsetDelta !== 0) {
        this.updateScrollState(
          {
            scrollLeftTs: this._scrollState.scrollLeftTs + offsetDeltaTs
          },
          true
        );
      }
      this.lensDrag(this._lensDragLastX);
    });
  }

  lensDragEnd() {
    this._lensDragging = false;
    if (this._rafWaiting) {
      window.cancelAnimationFrame(this._rafWaiting);
      this._rafWaiting = null;
    }
    if (this._pendingZoomAndScroll) {
      this.startAnimation();
    }
    this.clearDateTooltip();
  }

  resizeDragStart(x, fromLeft) {
    this.stopAnimation();
    this._resizeDragInitialLeftTs = this._scrollState.mainViewLeftTs;
    this._resizeDragInitialRightTs = this._scrollState.mainViewRightTs;
    this._resizeDragStartX = x;
    this._resizeDragFromLeft = fromLeft;
    this._resizeDragging = true;
  }

  resizeDrag(x) {
    const thisDelta = x - this._resizeDragLastX;
    if (thisDelta !== 0) {
      this._resizeDragLastMovementDelta = thisDelta;
    }
    this._resizeDragLastX = x;

    const xTs = x * this._scrollState.unitScale + this._scrollState.scrollLeftTs;

    let newLensLeftTs = this._resizeDragInitialLeftTs;
    let newLensRightTs = this._resizeDragInitialRightTs;

    if (this._resizeDragFromLeft) {
      newLensLeftTs = xTs;
    } else {
      newLensRightTs = xTs;
    }

    const minTs = calcLensLeftTsMinimum(this.props.xScrollPosition.canvas, this.props.zoom);
    const maxTs = calcLensRightTsMaximum(this.props.xScrollPosition.canvas, this.props.zoom);

    if (newLensLeftTs < minTs) {
      newLensLeftTs = minTs;
    }
    if (newLensRightTs > maxTs) {
      newLensRightTs = maxTs;
    }

    this._lastResizeDragLeftTs = newLensLeftTs;
    this._lastResizeDragRightTs = newLensRightTs;

    const newLensUnits = newLensRightTs - newLensLeftTs;
    const newLensWidth = newLensUnits / this._scrollState.unitScale;
    if (newLensWidth <= 20) {
      // Cannot go smaller than 20 pixels... so need to release and go again...
      return;
    }

    // Calculate the new unit scale to be applied to the main view
    let newUnitScale = newLensUnits / this.props.containerRect.width;

    // Don't allow scale beyond min/max bounds
    if (!_.isNil(this.props.zoomMinimum)) {
      if (newUnitScale < this.props.zoomMinimum) {
        newUnitScale = this.props.zoomMinimum;
      }
    }
    if (!_.isNil(this.props.zoomMaximum)) {
      if (newUnitScale > this.props.zoomMaximum) {
        newUnitScale = this.props.zoomMaximum;
      }
    }

    // If after all that nothing has changed, don't bother sending a notification or anything.
    if (newUnitScale === this._lastMainViewZoom) {
      return;
    }

    // Show tooltip
    if (this._resizeDragFromLeft) {
      const leftDate = dateWithTimestamp(this.props.calendar, Math.round(this._lastResizeDragLeftTs));
      const leftDateRounded = dateFunctions.roundedDateForScale(
        this.props.calendar,
        leftDate,
        newUnitScale * MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR
      );
      const leftDateString = stringFromDate(this.props.calendar, leftDateRounded);
      this.updateLeftDateTooltip(leftDateString);
    } else {
      const rightDate = dateWithTimestamp(this.props.calendar, Math.round(this._lastResizeDragRightTs));
      const rightDateRounded = dateFunctions.roundedDateForScale(
        this.props.calendar,
        rightDate,
        newUnitScale * MAXIMUM_CONTEXT_UNIT_SCALE_FACTOR
      );
      const rightDateString = stringFromDate(this.props.calendar, rightDateRounded);
      this.updateRightDateTooltip(rightDateString);
    }

    // Convert new left/right into a new unitScale and left position
    this._mainViewZoom = newUnitScale;
    this._mainViewScrollLeft = newLensLeftTs / this._mainViewZoom;

    this._lastMainViewZoom = this._mainViewZoom;

    this.notifyContainerOfViewport();

    if (this.lensResizeDragEdgeAnimationScrollDelta() !== 0) {
      this.lensResizeDragAnimateAtEdge();
    }

    // Force change to update context lens date tooltip
    this.setState({
      forceChange: uniqueId()
    });
  }

  lensResizeDragEdgeAnimationScrollDelta() {
    // If the lens is touching the left edge we need to repeat the drag with an increasing offset...
    if (!this._resizeDragging) {
      return 0;
    }

    // Stop handling the auto-scroll if the zoom has reached its maximum value
    if (!_.isNil(this.props.zoomMaximum)) {
      if (this._lastMainViewZoom >= this.props.zoomMaximum) {
        return 0;
      }
    }

    const minTs = calcLensLeftTsMinimum(this.props.xScrollPosition.canvas, this.props.zoom);
    const maxTs = calcLensRightTsMaximum(this.props.xScrollPosition.canvas, this.props.zoom);
    const atLeftEdge = this.props.hasLowerRangeLimit && this._lastResizeDragLeftTs <= minTs;
    const atRightEdge = this.props.hasUpperRangeLimit && this._lastResizeDragRightTs >= maxTs;

    if (!atLeftEdge && this._resizeDragLastX < AUTOSCROLL_BORDER && this._resizeDragLastMovementDelta <= 0) {
      return -5;
    } else if (
      !atRightEdge &&
      this._resizeDragLastX > this.props.containerRect.width - AUTOSCROLL_BORDER &&
      this._resizeDragLastMovementDelta >= 0
    ) {
      return 5;
    }
    return 0;
  }

  lensResizeDragAnimateAtEdge() {
    if (this._rafWaiting) {
      return;
    }
    this._rafWaiting = window.requestAnimationFrame(() => {
      this._rafWaiting = null;
      const offsetDelta = this.lensResizeDragEdgeAnimationScrollDelta();
      const offsetDeltaTs = offsetDelta * this._scrollState.unitScale;
      if (offsetDelta !== 0) {
        this.updateScrollState(
          {
            scrollLeftTs: this._scrollState.scrollLeftTs + offsetDeltaTs
          },
          true
        );
      }
      this.resizeDrag(this._resizeDragLastX);
    });
  }

  resizeDragEnd() {
    this._resizeDragging = false;
    if (this._rafWaiting) {
      window.cancelAnimationFrame(this._rafWaiting);
      this._rafWaiting = null;
    }
    if (this._pendingZoomAndScroll) {
      this.startAnimation();
    }
    this.clearDateTooltip();
  }

  onDoubleClick(e) {
    const offset = getScreenPointerPositionFromEvent(e).x - this.requestNodeClientLeft();
    const absoluteLensLeftTs = offset * this._scrollState.unitScale + this._scrollState.scrollLeftTs;

    this._mainViewZoom = this.props.zoom;
    this._mainViewScrollLeft = absoluteLensLeftTs / this._mainViewZoom - this.props.containerRect.width / 2;

    this.props.xScrollActions.animateToViewport(this._mainViewScrollLeft, null, this._mainViewZoom);
  }

  updateLeftDateTooltip(string) {
    if (this._contextLensTooltipRef) {
      this._contextLensTooltipRef.innerText = string;
      this._contextLensTooltipRef.classList.remove("contextLensRectRightTooltip");
      this._contextLensTooltipRef.classList.add("contextLensRectLeftTooltip");
    }
  }

  updateRightDateTooltip(string) {
    if (this._contextLensTooltipRef) {
      this._contextLensTooltipRef.innerText = string;
      this._contextLensTooltipRef.classList.remove("contextLensRectLeftTooltip");
      this._contextLensTooltipRef.classList.add("contextLensRectRightTooltip");
    }
  }

  clearDateTooltip() {
    this._contextLensTooltipRef.innerText = "";
    this._contextLensTooltipRef.classList.remove("contextLensRectLeftTooltip");
    this._contextLensTooltipRef.classList.remove("contextLensRectRightTooltip");
  }

  renderBar(scrollState) {
    const lensLeft = calcLensLeft(scrollState);
    const lensWidth = calcLensWidth(scrollState);

    const targetLensLeft = calcLensLeft(this._scrollState);
    const targetLensWidth = calcLensWidth(this._scrollState);

    // If we have arrived at our destination, turn off animation
    if (this._animate && lensLeft === targetLensLeft && lensWidth === targetLensWidth) {
      this.debouncedStopAnimation();
    }

    const barStyle = {
      left: `${this.props.containerRect.left}px`,
      top: `${this.props.containerRect.top}px`,
      width: `${this.props.containerRect.width}px`
    };

    return (
      <div
        className="contextBarView"
        style={barStyle}
        ref={node => {
          this._domNode = node;
        }}
        onDoubleClick={disableWhenIos(this.onDoubleClick)}
      >
        <ContextBarCanvas
          fontSize={this.props.fontSize}
          calendar={this.props.calendar}
          viewIds={this.props.viewIds}
          events={this.props.events}
          scrollLeftTs={scrollState.scrollLeftTs}
          unitScale={scrollState.unitScale}
          registerScrollListener={this.registerScrollListener}
          unregisterScrollListener={this.unregisterScrollListener}
          canvasRange={this.props.xScrollPosition.canvas.range}
        />
        <ContextBarLens
          width={lensWidth}
          left={lensLeft}
          requestParentLeft={this.requestNodeClientLeft}
          onDragStart={this.lensDragStart}
          onDrag={this.throttledLensDrag}
          onDragEnd={this.lensDragEnd}
          onResizeDragStart={this.resizeDragStart}
          onResizeDrag={this.throttledResizeDrag}
          onResizeDragEnd={this.resizeDragEnd}
          registerScrollListener={this.registerScrollListener}
          unregisterScrollListener={this.unregisterScrollListener}
          ref={node => (this._contextLensTooltipRef = node)}
        />
      </div>
    );
  }

  render() {
    const defaultPrecision = 0.1;
    const springPropsWithPrecision = precision => {
      return {
        stiffness: 95,
        damping: 25,
        precision
      };
    };
    let plainStyles = {
      scrollLeftTs: this._scrollState.scrollLeftTs,
      mainViewLeftTs: this._scrollState.mainViewLeftTs,
      mainViewRightTs: this._scrollState.mainViewRightTs,
      unitScale: this._scrollState.unitScale ? this._scrollState.unitScale : 1
    };
    // How big a jump is required to move a pixel?
    const leftPrecision = plainStyles.unitScale;

    // Work out which styles to actually use
    const motionStyles = this._animate
      ? {
        scrollLeftTs: spring(this._scrollState.scrollLeftTs, springPropsWithPrecision(leftPrecision)),
        mainViewLeftTs: spring(this._scrollState.mainViewLeftTs, springPropsWithPrecision(leftPrecision)),
        mainViewRightTs: spring(this._scrollState.mainViewRightTs, springPropsWithPrecision(defaultPrecision)),
        unitScale: spring(this._scrollState.unitScale, springPropsWithPrecision(defaultPrecision))
      }
      : plainStyles;

    return (
      <Motion defaultStyle={plainStyles} style={motionStyles}>
        {state => {
          return this.renderBar(state);
        }}
      </Motion>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    calendar: calendarSelectors.getCalendar(state.present),
    events: selectors.getContextViewEvents(state.present, props.viewIds),
    hasLowerRangeLimit: isDateValid(selectors.getActualDateFrom(state.present)),
    hasUpperRangeLimit: isDateValid(selectors.getActualDateTo(state.present))
  };
};

const mapDispatchToProps = _dispatch => {
  return {};
};

ContextBarView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  viewIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  events: ImmutablePropTypes.listOf(PropTypeShapes.contextEventDotRecord).isRequired,
  hasLowerRangeLimit: PropTypes.bool,
  hasUpperRangeLimit: PropTypes.bool,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,

  zoom: PropTypes.number,
  zoomMinimum: PropTypes.number,
  zoomMaximum: PropTypes.number,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withZoomContext(withContainerContext(withXScrollPositionContext(ContextBarView))));
